import { Check, ChevronDown, Shield } from 'lucide-react'
import { Button } from '@/components/shadcn/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '@/components/shadcn/dropdown-menu'

/** The modes a chat may pick. Bypass is chosen when a session starts, never from a chat (the box refuses it). */
export const PERMISSION_MODES = [
  { id: 'default', label: 'Ask first', note: 'Asks before edits and commands' },
  { id: 'acceptEdits', label: 'Accept edits', note: 'Edits files without asking' },
  { id: 'plan', label: 'Plan', note: 'Plans only, changes nothing' },
  { id: 'auto', label: 'Auto', note: 'Decides what is safe to run' },
] as const
export type PermissionMode = (typeof PERMISSION_MODES)[number]['id']

/**
 * Claude's permission mode beside the model (ONE-10). A headless session is
 * set to the exact mode; a terminal one only has Shift+Tab, which moves the
 * mode one step on, as in the terminal.
 */
export function PermissionPicker({ mode, disabled, onPick, onCycle }: {
  /** The session's mode, when it is known (headless sessions report it). */
  mode: string | null; disabled: boolean
  onPick?: (m: PermissionMode) => void
  onCycle?: () => void
}) {
  const current = PERMISSION_MODES.find((m) => m.id === mode)
  const label = current?.label ?? (mode === 'bypassPermissions' ? 'Bypass' : 'Permissions')
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-7 gap-1 rounded-full px-2 text-[11px] font-normal text-fg-subtle" disabled={disabled} aria-label="Permission mode">
          <Shield className="size-3" />{label}<ChevronDown className="size-3 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" side="top" className="w-64">
        <DropdownMenuLabel className="text-xs">Permission mode</DropdownMenuLabel>
        {onPick
          ? PERMISSION_MODES.map((m) => (
              <DropdownMenuItem key={m.id} className="gap-2 text-xs" onSelect={() => onPick(m.id)}>
                <Check className={m.id === mode ? 'size-3.5' : 'size-3.5 opacity-0'} />
                <span className="flex-1">{m.label}</span><span className="text-muted-foreground">{m.note}</span>
              </DropdownMenuItem>
            ))
          : <DropdownMenuItem className="flex-col items-start gap-0.5 text-xs" onSelect={() => onCycle?.()}>
              <span>Next mode (Shift+Tab)</span>
              <span className="text-muted-foreground">Ask first → Accept edits → Plan; the terminal's bottom line shows which</span>
            </DropdownMenuItem>}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
